import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MonitorPlay, Maximize2, Minimize2, ArrowLeft, Sun as SunIcon,
  Mic, Sparkles, Moon, SmilePlus, Plus, RotateCcw, ChevronRight,
  ShieldCheck, UtensilsCrossed, ListChecks, CalendarDays, MoonStar,
  CalendarHeart,
} from "lucide-react";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { useCalmMode } from "./CalmModeContext";
import { HomeOSAvatar } from "./components/HomeOSAvatar";
import { QuickCaptureModal } from "./components/QuickCaptureModal";
import { CalmModeModal } from "./CalmModeModal";
import { DelegateCleaningModal } from "./components/DelegateCleaningModal";
import { WeeklyResetModal, useWeeklyReset } from "./WeeklyReset";
import { WallScreensaver } from "./components/WallScreensaver";
import { SwapTaskModal } from "./components/SwapTaskModal";
import { ROOMS, MOOD_ZONES } from "./config/homeOsTheme";
import {
  getTodaysImportantItems,
  getFamilyMoodSummary,
  getNextCleaningTask,
  getTonightDinnerStatus,
  getApprovalItems,
  getWallCalendarPreview,
  getChoresSummary,
} from "./utils/homeOsSelectors";
import { getPendingHelpItems } from "./utils/taskSwapUtils";
import { getSuppliesSummary, getLowStockItems } from "./utils/suppliesUtils";
import { getDocumentSummary, getDocumentsDueSoon, getDocumentsNeedingAction } from "./utils/documentUtils";
import { HeartHandshake, PackageCheck, FolderOpen } from "lucide-react";
import { RenewalRadar } from "./components/RenewalRadar";
import { RenewalPrepCard } from "./components/RenewalPrepCard";

/* ════════════════════════════════════════════════════════════════════════
   FAMILY WALL DISPLAY MODE
   - Designed for tablets / kitchen wall touch screens.
   - Glanceable, big type, big touch targets.
   - Reuses existing Home OS state + modals (Quick Capture, Calm Mode, Delegate).
   ════════════════════════════════════════════════════════════════════════ */

export default function WallDisplayPage() {
  const navigate = useNavigate();
  const state = useHomeOSState();
  const { isCalm } = useCalmMode();
  const weeklyReset = useWeeklyReset();
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [calmOpen, setCalmOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [screensaverOpen, setScreensaverOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [swapTask, setSwapTask] = useState(null);
  const containerRef = useRef(null);
  const idleTimerRef = useRef(null);

  /* Any modal open? Block screensaver if so. */
  const anyModalOpen = quickCaptureOpen || calmOpen || delegateOpen || weeklyReset.open || !!swapTask;
  const screensaverMinutes = state.settings?.wallScreensaverMinutes ?? 2;

  /* Live clock — tick every 30s */
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  /* Fullscreen API */
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  /* ── Idle detection → activate screensaver ────────────────────────── */
  useEffect(() => {
    if (screensaverMinutes === 0 || anyModalOpen || screensaverOpen) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return undefined;
    }
    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setScreensaverOpen(true), screensaverMinutes * 60_000);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [screensaverMinutes, anyModalOpen, screensaverOpen]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (!containerRef.current?.requestFullscreen) {
          toast("Full screen is not available on this device.");
          return;
        }
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      toast("Full screen is not available on this device.");
    }
  };

  /* Derived data */
  const timeline = useMemo(() => getTodaysImportantItems(state, 7), [state]);
  const moodSummary = useMemo(() => getFamilyMoodSummary(state), [state]);
  const cleaning = useMemo(() => getNextCleaningTask(state), [state]);
  const dinner = useMemo(() => getTonightDinnerStatus(state), [state]);
  const approvals = useMemo(() => getApprovalItems(state, 4), [state]);
  const week = useMemo(() => getWallCalendarPreview(state), [state]);
  const chores = useMemo(() => getChoresSummary(state, 3), [state]);
  const helpPayload = useMemo(() => getPendingHelpItems(state, 3), [state]);
  const suppliesSum = useMemo(() => getSuppliesSummary(state.suppliesItems || []), [state.suppliesItems]);
  const lowSupplies = useMemo(() => getLowStockItems(state.suppliesItems || []).slice(0, 4), [state.suppliesItems]);
  const shoppingOpenCount = useMemo(() => (state.shoppingList || []).filter(it => !it.checked).length, [state.shoppingList]);
  const docsSummary = useMemo(() => getDocumentSummary(state.documents || []), [state.documents]);
  const docsDueSoon = useMemo(() => getDocumentsDueSoon(state.documents || [], 14).slice(0, 3), [state.documents]);
  const docsNeedsAction = useMemo(() => getDocumentsNeedingAction(state.documents || []).slice(0, 3), [state.documents]);

  const dateLabel = now.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
  const timeLabel = now.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", hour12: false });
  const householdMode = isCalm ? "Calm Mode Active" : (approvals.length >= 3 || timeline.length >= 6 ? "Busy Day" : "Normal");

  return (
    <div
      ref={containerRef}
      className="min-h-screen     dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 font-home"
      data-testid="wall-display-root"
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-6 lg:py-8 pb-36 lg:pb-40">

        {/* ════ HEADER ════ */}
        <header className="relative z-30 rounded-[28px] bg-white/85 dark:bg-slate-800/70 backdrop-blur border border-white/60 dark:border-slate-700/60 shadow-none p-5 sm:p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl    flex items-center justify-center shadow-md flex-shrink-0">
                <MonitorPlay className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-300">Family Wall</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-gray-50 tracking-tight leading-tight" data-testid="wall-household-name">
                  {state.settings?.householdName || "Our Home"}
                </h1>
                <p className="text-base sm:text-lg text-gray-500 dark:text-gray-300 mt-0.5" data-testid="wall-subtitle">
                  A shared household display for today's plan, mood, meals, cleaning and reminders.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="px-4 py-3 rounded-2xl    dark: dark: border border-amber-200 dark:border-amber-800" data-testid="wall-weather">
                <div className="flex items-center gap-2">
                  <SunIcon className="w-5 h-5 text-amber-600 dark:text-amber-300" strokeWidth={1.5} />
                  <p className="text-base font-extrabold text-amber-900 dark:text-amber-100">18°C · Fine</p>
                </div>
                <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300 mt-0.5">Live weather coming soon</p>
              </div>
              <div className="px-4 py-3 rounded-2xl bg-transparent dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800" data-testid="wall-clock">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-700 dark:text-violet-300">{dateLabel}</p>
                <p className="text-3xl font-extrabold text-violet-900 dark:text-violet-100 tabular-nums leading-tight">{timeLabel}</p>
              </div>
              <div className={`px-4 py-3 rounded-2xl border ${isCalm ? "bg-transparent border-indigo-200 dark:bg-indigo-900/30 dark:border-indigo-800" : householdMode === "Busy Day" ? "bg-transparent border-amber-200 dark:bg-amber-900/30 dark:border-amber-800" : "bg-transparent border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-800"}`} data-testid="wall-mode">
                <p className="text-[11px] font-extrabold uppercase tracking-wider opacity-80">Mode</p>
                <p className={`text-base font-extrabold ${isCalm ? "text-indigo-900 dark:text-indigo-100" : householdMode === "Busy Day" ? "text-amber-900 dark:text-amber-100" : "text-emerald-900 dark:text-emerald-100"}`}>{householdMode}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={toggleFullscreen}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-transparent hover:bg-teal-600 text-white text-xs font-extrabold shadow-md"
                  data-testid="wall-fullscreen-btn"
                >
                  {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  {isFullscreen ? "Exit full screen" : "Full screen"}
                </button>
                <div className="relative z-40">
                  <button
                    onClick={() => setSettingsOpen(s => !s)}
                    className="w-full inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-transparent dark:bg-indigo-900/30 hover:bg-indigo-200 text-indigo-700 dark:text-indigo-200 text-xs font-extrabold border border-indigo-200 dark:border-indigo-800"
                    data-testid="wall-screensaver-settings-btn"
                    aria-expanded={settingsOpen}
                  >
                    <MoonStar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Screensaver: {screensaverMinutes === 0 ? "Off" : `${screensaverMinutes} min`}
                  </button>
                  {settingsOpen && (
                    <div
                      className="absolute right-0 mt-2 w-60 bg-white dark:bg-slate-800 rounded-2xl shadow-none border border-gray-100 dark:border-slate-700 z-50 overflow-hidden"
                      data-testid="wall-screensaver-menu"
                    >
                      {[
                        { v: 0,  label: "Off" },
                        { v: 1,  label: "After 1 minute" },
                        { v: 2,  label: "After 2 minutes" },
                        { v: 5,  label: "After 5 minutes" },
                        { v: 10, label: "After 10 minutes" },
                      ].map(opt => {
                        const active = screensaverMinutes === opt.v;
                        return (
                          <button
                            key={opt.v}
                            onClick={() => { state.updateSettings({ wallScreensaverMinutes: opt.v }); setSettingsOpen(false); }}
                            className={`w-full text-left px-4 py-2.5 text-sm font-bold transition-colors ${active ? "bg-transparent text-white" : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"}`}
                            data-testid={`wall-screensaver-opt-${opt.v}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => { setSettingsOpen(false); setScreensaverOpen(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-extrabold text-indigo-700 dark:text-indigo-300 border-t border-gray-100 dark:border-slate-700 hover:bg-transparent dark:hover:bg-indigo-900/20"
                        data-testid="wall-screensaver-preview"
                      >
                        Preview screensaver
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => navigate("/home-os")}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white dark:bg-slate-700 hover:bg-gray-50 text-gray-700 dark:text-gray-200 text-xs font-extrabold border border-gray-200 dark:border-slate-600"
                  data-testid="wall-exit-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Exit Wall Display
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* ════ MAIN GRID — 3 columns landscape, stack on mobile ════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── LEFT COLUMN ─────────────────────────────────── */}
          <div className="space-y-6">

            {/* Family Mood */}
            <Panel
              testId="wall-panel-mood"
              tone="rose"
              icon={SmilePlus}
              title="Family"
              subtitle={`${moodSummary.checkedIn} of ${moodSummary.total} checked in today`}
              cta={{ label: "Mood Check-In", onClick: () => navigate("/home-os/mood"), testId: "wall-mood-cta" }}
            >
              <div className="grid grid-cols-2 gap-2.5">
                {moodSummary.memberMoods.map(({ member, checkin }) => {
                  const z = checkin ? MOOD_ZONES[checkin.zone] : null;
                  return (
                    <button
                      key={member.id}
                      onClick={() => navigate("/home-os/mood")}
                      className={`text-left rounded-2xl p-3 border-2 transition-all hover:shadow-md ${
                        z ? `${z.tile}` : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700"
                      }`}
                      data-testid={`wall-mood-member-${member.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <HomeOSAvatar member={member} size="md" />
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-extrabold text-gray-900 dark:text-gray-50 truncate">{member.name}</p>
                          {checkin ? (
                            <p className={`text-xs font-extrabold ${z.accent} truncate`}>
                              {checkin.moodLabel} · {checkin.energy}
                            </p>
                          ) : (
                            <p className="text-xs font-extrabold text-gray-400 truncate">Tap to check in</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Panel>

            {/* Who's home / Calendar preview */}
            <Panel
              testId="wall-panel-calendar"
              tone="violet"
              icon={CalendarDays}
              title="This week"
              subtitle="Quick calendar snapshot"
              cta={{ label: "Open Calendar", onClick: () => navigate("/home-os/calendar"), testId: "wall-calendar-cta" }}
            >
              <div className="grid grid-cols-7 gap-1.5" data-testid="wall-week-strip">
                {week.map(d => (
                  <div
                    key={d.iso}
                    className={`rounded-xl p-2 text-center border ${d.isToday ? "bg-transparent text-white border-violet-500 shadow-md" : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200"}`}
                    data-testid={`wall-day-${d.iso}`}
                  >
                    <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-80">{d.label}</p>
                    <p className="text-xl font-extrabold leading-none mt-0.5">{d.dayNumber}</p>
                    <div className="flex items-center justify-center gap-0.5 mt-1.5 h-2">
                      {d.modules.map((m, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${m.color.dot}`} />)}
                    </div>
                    {d.count > 0 && (
                      <p className={`text-[9px] font-extrabold mt-0.5 ${d.isToday ? "text-white/90" : "text-gray-500"}`}>{d.count}</p>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {/* ── MIDDLE COLUMN ─────────────────────────────────── */}
          <div className="space-y-6">

            {/* Today timeline */}
            <Panel
              testId="wall-panel-timeline"
              tone="amber"
              icon={SunIcon}
              title="Today"
              subtitle={dateLabel}
              cta={{ label: "View full day", onClick: () => navigate("/home-os"), testId: "wall-timeline-cta" }}
            >
              {timeline.length === 0 ? (
                <p className="text-base text-gray-500 italic">Nothing scheduled today. Enjoy the calm.</p>
              ) : (
                <ul className="space-y-2">
                  {timeline.map(item => {
                    const mod = item.module;
                    const isDone = item.status === "Done";
                    const member = state.members.find(m => m.id === item.assignedTo);
                    return (
                      <li
                        key={item.id}
                        className={`rounded-2xl border px-3 py-3 flex items-center gap-3 ${mod.color.tile}`}
                        data-testid={`wall-timeline-item-${item.id}`}
                      >
                        <div className="w-14 text-center flex-shrink-0">
                          <p className={`text-base font-extrabold tabular-nums ${isDone ? "text-gray-400 line-through" : "text-gray-900 dark:text-gray-50"}`}>
                            {item.dueTime || "—"}
                          </p>
                        </div>
                        <span className={`w-2 h-2 rounded-full ${mod.color.dot} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-base font-extrabold leading-tight ${isDone ? "text-gray-400 line-through" : "text-gray-900 dark:text-gray-50"}`}>{item.title}</p>
                          <p className={`text-xs font-extrabold ${mod.color.textAccent}`}>{mod.label}</p>
                        </div>
                        {member && <HomeOSAvatar member={member} size="sm" showRing={false} />}
                        {item.approvalRequired && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-transparent text-rose-700 dark:bg-rose-900/30 dark:text-rose-200 text-[10px] font-extrabold">
                            <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />Approval
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </div>

          {/* ── RIGHT COLUMN ─────────────────────────────────── */}
          <div className="space-y-6">

            {/* Dinner */}
            <Panel
              testId="wall-panel-dinner"
              tone="emerald"
              icon={UtensilsCrossed}
              title="Dinner"
              subtitle={dinner.status}
              cta={{ label: "Sort dinner", onClick: () => navigate("/home-os/meals"), testId: "wall-dinner-cta" }}
            >
              <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-white/70 dark:bg-slate-800/60 p-4">
                <p className="text-2xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{dinner.title}</p>
                {dinner.time && <p className="text-sm font-bold text-gray-500 mt-1">at {dinner.time}</p>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <button onClick={() => navigate("/home-os/meals")} className="px-3 py-1.5 rounded-full bg-transparent dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 text-xs font-extrabold hover:bg-emerald-200" data-testid="wall-dinner-add">Add meal</button>
                <button onClick={() => navigate("/home-os/supplies")} className="px-3 py-1.5 rounded-full bg-transparent dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 text-xs font-extrabold hover:bg-amber-200" data-testid="wall-dinner-shopping">Shopping list</button>
              </div>
            </Panel>

            {/* Cleaning */}
            <Panel
              testId="wall-panel-cleaning"
              tone="cyan"
              icon={Sparkles}
              title="Cleaning"
              subtitle={cleaning.openCount > 0 ? `${cleaning.openCount} open tasks` : "All caught up"}
              cta={{ label: "Delegate cleaning", onClick: () => setDelegateOpen(true), testId: "wall-cleaning-delegate" }}
            >
              {cleaning.next && (
                <div className="rounded-2xl bg-white/70 dark:bg-slate-800/60 border border-cyan-200 dark:border-cyan-800 p-3 mb-2">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-cyan-700 dark:text-cyan-300">Next up</p>
                  <p className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{cleaning.next.title}</p>
                  <p className="text-xs font-bold text-gray-500 mt-0.5">{ROOMS[cleaning.next.room]?.label} · {cleaning.next.estimatedMinutes}m · {cleaning.next.energy} energy</p>
                  <button
                    onClick={() => setSwapTask({ task: cleaning.next, kind: "cleaning" })}
                    className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-transparent hover:bg-pink-600 text-white border border-pink-500 text-[10px] font-extrabold shadow-sm"
                    data-testid="wall-cleaning-help-out"
                  >
                    <HeartHandshake className="w-3 h-3" strokeWidth={1.5} />
                    I'll take this
                  </button>
                </div>
              )}
              {cleaning.topRoom && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-extrabold">{ROOMS[cleaning.topRoom.roomId]?.label}</span> has {cleaning.topRoom.count} open task{cleaning.topRoom.count === 1 ? "" : "s"}.
                </p>
              )}
              <button onClick={() => navigate("/home-os/cleaning")} className="mt-2 text-xs font-extrabold text-cyan-700 dark:text-cyan-300 hover:underline" data-testid="wall-cleaning-open">Open Cleaning →</button>
            </Panel>

            {/* Chores */}
            <Panel
              testId="wall-panel-chores"
              tone="orange"
              icon={ListChecks}
              title="Chores"
              subtitle={`${chores.openCount} open · ${chores.assignedToday.length} assigned today`}
              cta={{ label: "Open Chores", onClick: () => navigate("/home-os/chores"), testId: "wall-chores-cta" }}
            >
              {chores.next.length === 0 ? (
                <p className="text-base text-gray-500 italic">No chores queued. Nice.</p>
              ) : (
                <ul className="space-y-1.5">
                  {chores.next.map(t => {
                    const member = state.members.find(m => m.id === t.assignedTo);
                    return (
                      <li key={t.id} className="flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-orange-100 dark:border-orange-800 px-3 py-2" data-testid={`wall-chore-${t.id}`}>
                        {member ? <HomeOSAvatar member={member} size="xs" showRing={false} /> : <span className="w-6 h-6 rounded-full bg-gray-200" />}
                        <p className="text-base font-extrabold text-gray-800 dark:text-gray-100 truncate flex-1">{t.title}</p>
                        <span className="text-[10px] font-extrabold text-gray-400">{t.dueDate?.slice(5)}</span>
                        <button
                          onClick={() => setSwapTask({ task: t, kind: "task" })}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-transparent hover:bg-pink-600 text-white text-[10px] font-extrabold shadow-sm"
                          data-testid={`wall-chore-help-${t.id}`}
                        >
                          <HeartHandshake className="w-2.5 h-2.5" strokeWidth={1.5} />
                          I'll take this
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>

            {/* Help out — pending swap opportunities */}
            {helpPayload.total > 0 && (
              <Panel
                testId="wall-panel-help-out"
                tone="pink"
                icon={HeartHandshake}
                title="Help out"
                subtitle={helpPayload.total === 1 ? "1 task someone could use backup on" : `${helpPayload.total} tasks someone could use backup on`}
                cta={{ label: "Open Chores", onClick: () => navigate("/home-os/chores"), testId: "wall-help-out-cta" }}
              >
                <ul className="space-y-1.5" data-testid="wall-help-out-list">
                  {helpPayload.items.map(it => (
                    <li
                      key={it.id}
                      className="flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-pink-200 dark:border-pink-800/60 px-3 py-2"
                      data-testid={`wall-help-out-item-${it.id}`}
                    >
                      {it.assignee ? (
                        <HomeOSAvatar member={it.assignee} size="xs" showRing={false} />
                      ) : (
                        <span className="w-6 h-6 rounded-full bg-gray-200" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-extrabold text-gray-800 dark:text-gray-100 truncate leading-tight">{it.title}</p>
                        <p className="text-[10px] font-bold text-pink-700 dark:text-pink-300 truncate">
                          {it.assignee?.name || "Unassigned"} · {it.categoryOrRoom}
                        </p>
                      </div>
                      <button
                        onClick={() => setSwapTask({ task: it.raw, kind: it.kind })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-transparent hover:bg-pink-600 text-white text-[10px] font-extrabold shadow-sm"
                        data-testid={`wall-help-out-take-${it.id}`}
                      >
                        <HeartHandshake className="w-2.5 h-2.5" strokeWidth={1.5} />
                        I'll take this
                      </button>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            {/* Approvals */}
            <Panel
              testId="wall-panel-approvals"
              tone="rose"
              icon={ShieldCheck}
              title="Approvals & reminders"
              subtitle={approvals.length === 0 ? "Nothing waiting" : `${approvals.length} need review`}
              cta={approvals.length > 0 ? { label: "Review approvals", onClick: () => navigate("/home-os"), testId: "wall-approvals-cta" } : null}
            >
              {approvals.length === 0 ? (
                <p className="text-base text-gray-500 italic">Inbox zero — well done.</p>
              ) : (
                <ul className="space-y-1.5">
                  {approvals.map(a => (
                    <li key={a.id} className={`rounded-xl border px-3 py-2 ${a.module.color.tile}`} data-testid={`wall-approval-${a.id}`}>
                      <p className={`text-[10px] font-extrabold uppercase tracking-wider ${a.module.color.textAccent}`}>{a.module.label}</p>
                      <p className="text-base font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{a.title}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Supplies — low stock + shopping list */}
            <Panel
              testId="wall-panel-supplies"
              tone="yellow"
              icon={PackageCheck}
              title="Supplies"
              subtitle={
                suppliesSum.needsBuying === 0
                  ? `All stocked · ${shoppingOpenCount} on shopping list`
                  : `${suppliesSum.needsBuying} running low · ${shoppingOpenCount} on shopping list`
              }
              cta={{ label: "Open Supplies", onClick: () => navigate("/home-os/supplies"), testId: "wall-supplies-cta" }}
            >
              {lowSupplies.length === 0 ? (
                <p className="text-base text-gray-500 italic">Nothing running low. Nice.</p>
              ) : (
                <ul className="space-y-1.5">
                  {lowSupplies.map(it => (
                    <li key={it.id} className="flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-amber-100 dark:border-amber-800 px-3 py-2" data-testid={`wall-supply-${it.id}`}>
                      <span className={`w-2 h-2 rounded-full ${it.status === "Out" ? "bg-transparent" : "bg-transparent"}`} />
                      <p className="text-base font-extrabold text-gray-800 dark:text-gray-100 truncate flex-1">{it.name}</p>
                      <span className="text-[10px] font-extrabold text-gray-400">{it.category}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Documents & Admin — needs action + due soon */}
            <Panel
              testId="wall-panel-documents"
              tone="violet"
              icon={FolderOpen}
              title="Admin"
              subtitle={
                docsSummary.needsAction === 0 && docsSummary.dueSoon === 0
                  ? "Inbox clear"
                  : `${docsSummary.needsAction} need action · ${docsSummary.dueSoon} due soon`
              }
              cta={{ label: "Open Documents", onClick: () => navigate("/home-os/documents"), testId: "wall-documents-cta" }}
            >
              {docsNeedsAction.length === 0 && docsDueSoon.length === 0 ? (
                <p className="text-base text-gray-500 italic">All paperwork is up to date.</p>
              ) : (
                <ul className="space-y-1.5">
                  {(() => {
                    // A document can qualify for both "needs action" and "due soon" —
                    // dedupe by id so React doesn't get duplicate keys on the wall.
                    const seen = new Set();
                    const merged = [...docsNeedsAction, ...docsDueSoon].filter(d => {
                      if (seen.has(d.id)) return false;
                      seen.add(d.id);
                      return true;
                    }).slice(0, 4);
                    return merged.map(d => {
                      const ref = d.dueDate || d.renewalDate || d.reminderDate;
                      return (
                        <li key={d.id} className="flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-violet-100 dark:border-violet-800 px-3 py-2" data-testid={`wall-document-${d.id}`}>
                          <span className="w-2 h-2 rounded-full bg-transparent" />
                          <p className="text-base font-extrabold text-gray-800 dark:text-gray-100 truncate flex-1">{d.title}</p>
                          {ref && <span className="text-[10px] font-extrabold text-violet-700 dark:text-violet-300">{ref.slice(5)}</span>}
                        </li>
                      );
                    });
                  })()}
                </ul>
              )}
              <RenewalRadar variant="wall" />
              <RenewalPrepCard variant="wall" onOpenDocument={() => navigate("/home-os/documents")} />
            </Panel>
          </div>
        </div>

        {/* ════ QUICK ACTIONS DOCK ════ */}
        <div className="sticky bottom-4 mt-8" data-testid="wall-action-dock">
          <div className="rounded-[28px] bg-white/95 dark:bg-slate-800/95 backdrop-blur border border-white/60 dark:border-slate-700/60 shadow-none p-3 sm:p-4 flex items-center gap-2 sm:gap-3 overflow-x-auto">
            <DockButton icon={Sparkles}   label="Ask ARA"        color=" " onClick={() => navigate("/home-os/command-centre")} testId="wall-dock-ask" />
            <DockButton icon={Mic}        label="Quick Capture"  color=" "   onClick={() => setQuickCaptureOpen(true)}            testId="wall-dock-capture" />
            <DockButton icon={SmilePlus}  label="Mood"           color="  " onClick={() => navigate("/home-os/mood")}    testId="wall-dock-mood" />
            <DockButton icon={Moon}       label="Calm Mode"      color=" "    onClick={() => setCalmOpen(true)}                    testId="wall-dock-calm" />
            <DockButton icon={Plus}       label="Add Task"       color=" "   onClick={() => setQuickCaptureOpen(true)}            testId="wall-dock-add" />
            <DockButton icon={RotateCcw}  label="Weekly Reset"   color=" "       onClick={weeklyReset.start}                          testId="wall-dock-reset" />
            <DockButton icon={CalendarHeart} label="This Week" color="  " onClick={() => navigate("/home-os/report")} testId="wall-dock-report" />
          </div>
        </div>

      </div>

      <QuickCaptureModal open={quickCaptureOpen} onClose={() => setQuickCaptureOpen(false)} />
      <CalmModeModal open={calmOpen} onClose={() => setCalmOpen(false)} />
      <DelegateCleaningModal open={delegateOpen} onClose={() => setDelegateOpen(false)} />
      <WeeklyResetModal open={weeklyReset.open} onClose={weeklyReset.close} />
      <WallScreensaver open={screensaverOpen} state={state} onExit={() => setScreensaverOpen(false)} />
      <SwapTaskModal
        open={!!swapTask}
        task={swapTask?.task || null}
        taskKind={swapTask?.kind || "task"}
        onClose={() => setSwapTask(null)}
      />
    </div>
  );
}

/* ── Panel primitive (large card) ────────────────────────────────────── */

const PANEL_TONES = {
  rose:    { ring: "border-rose-200 dark:border-rose-800",       iconBg: "  ",       accent: "text-rose-700 dark:text-rose-300" },
  violet:  { ring: "border-violet-200 dark:border-violet-800",   iconBg: "  ",  accent: "text-violet-700 dark:text-violet-300" },
  amber:   { ring: "border-amber-200 dark:border-amber-800",     iconBg: "  ",    accent: "text-amber-700 dark:text-amber-300" },
  emerald: { ring: "border-emerald-200 dark:border-emerald-800", iconBg: "  ",   accent: "text-emerald-700 dark:text-emerald-300" },
  cyan:    { ring: "border-cyan-200 dark:border-cyan-800",       iconBg: "  ",        accent: "text-cyan-700 dark:text-cyan-300" },
  orange:  { ring: "border-orange-200 dark:border-orange-800",   iconBg: "  ",    accent: "text-orange-700 dark:text-orange-300" },
  pink:    { ring: "border-pink-200 dark:border-pink-800",       iconBg: "  ",       accent: "text-pink-700 dark:text-pink-300" },
  yellow:  { ring: "border-amber-200 dark:border-amber-800",     iconBg: "  ",    accent: "text-amber-700 dark:text-amber-300" },
};

function Panel({ testId, tone, icon: Icon, title, subtitle, cta, children }) {
  const t = PANEL_TONES[tone] || PANEL_TONES.violet;
  return (
    <section className={`rounded-[28px] bg-white/85 dark:bg-slate-800/70 backdrop-blur border-2 ${t.ring} shadow-md p-5 sm:p-6`} data-testid={testId}>
      <header className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-2xl ${t.iconBg} flex items-center justify-center shadow-sm flex-shrink-0`}>
          <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{title}</h2>
          <p className={`text-sm font-bold ${t.accent}`}>{subtitle}</p>
        </div>
        {cta && (
          <button
            onClick={cta.onClick}
            className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-extrabold ${t.accent} bg-white dark:bg-slate-700 border border-current/20 hover:bg-gray-50 dark:hover:bg-slate-600 flex-shrink-0`}
            data-testid={cta.testId}
          >
            {cta.label} <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

/* ── Dock button (big, touch-friendly) ──────────────────────────────── */

function DockButton({ icon: Icon, label, color, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 min-w-[100px] flex flex-col items-center justify-center gap-1 rounded-2xl py-3 px-2 hover:bg-gray-50 dark:hover:bg-slate-700 active:scale-95 transition-transform"
      data-testid={testId}
    >
      <span className={`w-12 h-12 rounded-2xl  ${color} flex items-center justify-center shadow-md`}>
        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
      </span>
      <span className="text-xs sm:text-sm font-extrabold text-gray-700 dark:text-gray-200 text-center leading-tight">{label}</span>
    </button>
  );
}
